/**
 * Parses the multi-line panel breakdown out of a shot's `description` field.
 *
 * The storyboard generator (see `/app/docs/SHOT_GENERATION_RULES_SPEC.md`)
 * formats each shot's `description` like:
 *
 *   Panel 1 (00:00 – 00:02.5):
 *     - Duration: 2.5s
 *     - Story Purpose: ...
 *     - Visual Composition: ...
 *     - Main Elements: ...
 *     - Camera Movement: ...
 *     - Text On Screen: ...
 *     - Voice Over: ...
 *
 *   Panel 2 (00:02.5 – 00:05):
 *     ...
 *
 * This utility extracts those panel blocks into structured objects the
 * Storyboard UI can render as a horizontal strip of cards, and builds a ready-
 * to-use image-generation prompt per panel (concatenation of Visual Composition
 * + Main Elements + Camera Movement so the LLM has the visual hooks it needs).
 *
 * Pure JS, no React. Returns `[]` if no panels are found — the Storyboard UI
 * falls back to the legacy "Start Frame / End Frame" layout in that case
 * (or, for new projects, just shows an empty strip the user can populate).
 *
 * @param {string} description - Shot's `description` field
 * @returns {Array<{ index, timecode, duration, storyPurpose, visualComposition, mainElements, cameraMovement, textOnScreen, voiceOver, prompt }>}
 */
export const parsePanelsFromDescription = (description) => {
    if (!description || typeof description !== 'string') return [];

    // Feb 13 2026 — more permissive panel header detection. Real-world LLM
    // outputs sometimes drop the parens around the timecode, wrap the
    // header in markdown bold, or use "Panel 1:" instead of the fully-
    // formatted variant. Additionally they OFTEN emit all panels inline as
    // one long paragraph rather than on separate lines, so the split
    // pattern MUST work on inline content — not just start-of-line.
    //
    // Matches:
    //   Panel 1 (00:00 - 00:02.5):
    //   Panel 1 (00:00-00:02.5)
    //   **Panel 1** (00:00-00:02.5):
    //   Panel 1:
    //   PANEL 1 —
    //   [Panel 1] 00:00-00:02.5
    //
    // Strip any leading `**`, `[`, or `#` so the split works regardless
    // of the surrounding markdown decoration. Also strip trailing `**`
    // that wraps the whole header (e.g. `**Panel 1**`).
    const cleaned = description
        .replace(/\*+\s*Panel\s+(\d+)\s*\*+/gi, 'Panel $1')   // handles **Panel 1**
        .replace(/[#*[]+\s*Panel/gi, 'Panel');                // handles [Panel or #Panel or *Panel
    // Split on ANY occurrence of `Panel <n>` followed by either `(` (for a
    // parenthesised timecode) OR `:` OR `—` — anywhere in the string, not
    // just at start of line. This handles the common LLM behaviour of
    // emitting the whole shot description as a single flowing paragraph.
    // A leading word-boundary keeps us from matching mid-word like "panelist".
    const panelHeader = /(?=\bPanel\s+\d+\s*[(:—-])/i;
    const blocks = cleaned.split(panelHeader).filter(b => /^\s*Panel\s+\d+\s*[(:—-]/i.test(b));

    if (blocks.length === 0) {
        // Diagnostic: help future debugging when the parser can't find any
        // panels but the description was clearly supposed to have them.
        if (description.length > 200) {
            console.warn(
                '[panelParser] Parsed 0 panels from a',
                description.length,
                'char description. Preview:',
                description.slice(0, 250),
                '…',
            );
        }
        return [];
    }

    return blocks.map((block, i) => {
        // Header now accepts either `Panel N (timecode)` or `Panel N:` /
        // `Panel N —` without a parenthesised timecode.
        const headerMatch = block.match(/Panel\s+(\d+)\s*(?:\(\s*([^)]+?)\s*\))?/i);
        const index = headerMatch ? parseInt(headerMatch[1], 10) : i + 1;
        const timecode = headerMatch && headerMatch[2] ? headerMatch[2].trim() : '';

        const fieldValue = (label) => {
            // Accept `- Label:`, `* Label:`, `• Label:`, `**Label:**`, and
            // plain `Label:` with any leading whitespace / markdown.
            // Value continues until the next label-like token, a period-
            // then-capital sentence break inside a run-on paragraph, or a
            // newline — so a paragraph with `Duration: 3s. Story Purpose: …`
            // gets parsed field-by-field instead of everything spilling
            // into `duration`.
            const re = new RegExp(
                `(?:^|\\n|\\.\\s|,\\s|;\\s|\\|\\s)\\s*[-•*]?\\s*(?:\\*\\*)?\\s*${label}\\s*(?:\\*\\*)?\\s*:\\s*` +
                // value: stop at the next known label, newline, or a
                // ". Capital-Word" boundary that starts another labelled field.
                `((?:(?!\\n|Duration\\s*:|Story\\s+Purpose\\s*:|Visual\\s+Composition\\s*:|Main\\s+Elements\\s*:|Camera\\s+Movement\\s*:|Text\\s+On\\s+Screen\\s*:|Voice\\s+Over\\s*:|Panel\\s+\\d+).)+?)` +
                `(?=\\n|Duration\\s*:|Story\\s+Purpose\\s*:|Visual\\s+Composition\\s*:|Main\\s+Elements\\s*:|Camera\\s+Movement\\s*:|Text\\s+On\\s+Screen\\s*:|Voice\\s+Over\\s*:|Panel\\s+\\d+|$)`,
                'i',
            );
            const m = block.match(re);
            if (!m) return '';
            // Strip leading `**`, trailing `**`, trailing punctuation.
            return m[1]
                .replace(/^\s*\*+\s*/, '')
                .replace(/\s*\*+\s*$/, '')
                .replace(/[.,;]\s*$/, '')
                .trim();
        };

        const duration         = fieldValue('Duration');
        const storyPurpose     = fieldValue('Story Purpose');
        const visualComposition= fieldValue('Visual Composition');
        const mainElements     = fieldValue('Main Elements');
        const cameraMovement   = fieldValue('Camera Movement');
        const textOnScreen     = fieldValue('Text On Screen');
        const voiceOver        = fieldValue('Voice Over');

        // Fallback: strip the header from the block and keep the raw
        // free text (with any labelled `Field:` prefixes removed). This
        // is used both when NO labelled fields matched AND as a rescue
        // when the labelled path produced an empty prompt (e.g. the LLM
        // only emitted `Voice Over: none` for the last panel and nothing
        // else — anyLabelled = true but the four "visual" fields used
        // for the prompt are all empty). Without this rescue the last
        // panel silently rendered "No prompt yet" even though the shot
        // description clearly had panel content.
        const stripHeader = block
            .replace(/^\s*Panel\s+\d+\s*(?:\(\s*[^)]+?\s*\))?\s*[:—-]?\s*/i, '');
        // Also remove any labelled `Field:` prefixes so the free text
        // becomes clean prose. e.g. `- Voice Over: none` becomes ``.
        const freeText = stripHeader
            .replace(/(?:^|\n|[.;])\s*[-•*]?\s*(?:\*\*)?\s*(?:Duration|Story\s+Purpose|Visual\s+Composition|Main\s+Elements|Camera\s+Movement|Text\s+On\s+Screen|Voice\s+Over)\s*(?:\*\*)?\s*:\s*[^\n]*/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

        const anyLabelled = [duration, storyPurpose, visualComposition, mainElements, cameraMovement, textOnScreen, voiceOver]
            .some(v => v && v.length > 0);

        // Build a ready-to-use image-gen prompt: cinematic still + the panel's
        // visual hooks. Voice Over and Text On Screen are intentionally NOT
        // added — they belong to the video/SFX layer, not the still frame.
        let prompt = anyLabelled
            ? [
                visualComposition && `Cinematic still: ${visualComposition}.`,
                mainElements      && `Featuring ${mainElements}.`,
                cameraMovement    && `Framing: ${cameraMovement}.`,
                storyPurpose      && `Mood / intent: ${storyPurpose}.`,
            ].filter(Boolean).join(' ')
            : '';

        // Rescue #1: if the labelled path produced an empty prompt but we
        // have free-text content in the block, use that. Guarantees panels
        // are never silently empty when the source description clearly has
        // panel content.
        if (!prompt && freeText) {
            prompt = `Cinematic still: ${freeText}`;
        }
        // Rescue #2: if we still have no prompt but the LLM emitted a
        // story purpose (the last panel edge-case reported Feb 13 2026),
        // use that as the sole prompt anchor. Better than "No prompt yet"
        // which forces the user to manually re-write the panel.
        if (!prompt && storyPurpose) {
            prompt = `Cinematic still: ${storyPurpose}`;
        }
        // Rescue #3: last-ditch — if we still have literally nothing usable
        // and there's any non-noise labelled field, at least surface it so
        // the panel isn't blank in the UI.
        if (!prompt) {
            const usable = [visualComposition, mainElements, cameraMovement, storyPurpose, textOnScreen, voiceOver]
                .filter(v => v && v.length > 0 && !/^\s*(none|n\/a|-)\s*$/i.test(v));
            if (usable.length > 0) {
                prompt = `Cinematic still: ${usable[0]}`;
            } else {
                console.warn(
                    `[panelParser] Panel ${index}: could not extract any usable content from the block. ` +
                    `Backend LLM likely emitted an incomplete panel. Raw block:`,
                    block,
                );
            }
        }

        // Use the strongest visualComposition available:
        // 1. Labelled `Visual Composition:` value if present.
        // 2. Otherwise the free text after stripping labelled prefixes.
        const finalVisual = visualComposition || freeText || '';

        return {
            index,
            timecode,
            duration,
            storyPurpose,
            visualComposition: finalVisual,
            mainElements,
            cameraMovement,
            textOnScreen,
            voiceOver,
            prompt,
        };
    });
};

export default parsePanelsFromDescription;

// ──────────────────────────────────────────────────────────────────────
// Entity auto-detection
//
// Given a panel and the project's character library, returns the IDs of
// the characters and elements whose names are mentioned in the panel's
// natural-language description. This drives image-generation references
// when the user has not made an explicit per-panel override.
//
// Match strategy:
//   • Case-insensitive
//   • Whole-word (so "Bob" doesn't match "Bobcat")
//   • Searches across Main Elements + Visual Composition + Story Purpose
//
// Why those three fields: Main Elements is the canonical list, but
// generators frequently mention a character by name in Visual Composition
// ("Elara stands centre frame…") or Story Purpose ("introduce Elara…").
// Casting a slightly wider net keeps the auto-detection robust.
// ──────────────────────────────────────────────────────────────────────

/**
 * Escape a string for safe use inside a RegExp.
 */
const escapeRegExp = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Returns { characterIds, elementIds } detected from a panel's text fields.
 *
 * @param {Object} panel - Parsed panel object (output of parsePanelsFromDescription)
 * @param {Array<{ id, name, category }>} projectCharacters - The project's character library
 * @returns {{ characterIds: string[], elementIds: string[] }}
 */
export const detectEntityIdsInPanel = (panel, projectCharacters) => {
    const empty = { characterIds: [], elementIds: [] };
    if (!panel || !Array.isArray(projectCharacters) || projectCharacters.length === 0) return empty;

    const haystack = [
        panel.mainElements,
        panel.visualComposition,
        panel.storyPurpose,
    ].filter(Boolean).join(' ').toLowerCase();
    if (!haystack) return empty;

    const characterIds = [];
    const elementIds = [];

    for (const entity of projectCharacters) {
        const name = (entity?.name || '').trim();
        if (!name) continue;
        // Whole-word, case-insensitive match.
        const re = new RegExp(`\\b${escapeRegExp(name.toLowerCase())}\\b`);
        if (re.test(haystack)) {
            if (entity.category === 'element') elementIds.push(entity.id);
            else characterIds.push(entity.id);
        }
    }

    return { characterIds, elementIds };
};
